import LocalizedLink from '@components/Localization/LocalizedLink'
import {
  Box,
  Button,
  Checklist,
  OldHeading,
  Image,
  OldText,
  VideoPlayer,
  breakpoints,
} from '@components/gassan-ui'
import { getKontentImage } from '@lib/kontent'
import { TourPage } from '@lib/kontent/models/TourPage'
import { useTranslation } from 'next-i18next'
import { rem } from 'polished'
import { FC } from 'react'
import styled from 'styled-components'

const Content = styled(Box)`
  @media screen and (min-width: ${breakpoints.large}) {
    transform: translateY(-50%);
  }
`

type Props = {
  item: TourPage
  variant: 'text-video' | 'video-text'
}

const FeaturedTour: FC<Props> = ({ item, variant }) => {
  const { t } = useTranslation('other')
  const isTextVideo = variant === 'text-video'
  const listItems = item.featured_tour_highlights.value
    .split('\n')
    .filter((t) => t !== '')
    .slice(0, 4)
  if (
    !item.featured_tour_placeholder.value[0] ||
    !item.featured_tour_description.value ||
    !item.featured_tour_highlights.value
  )
    return null
  return (
    <Box
      display={{ _: 'flex', large: 'grid', xlarge: 'flex' }}
      gridTemplateColumns="1fr 1fr"
      alignItems={{ large: 'center' }}
      gridColumnGap="1.5rem"
      position="relative"
      maxWidth={rem(1450)}
      width={{ _: '100%', large: 'calc(100% - 3rem)', xlarge: 'calc(100% - 5.5rem)' }}
      m="0 auto"
      justifyContent={{ large: isTextVideo ? 'flex-end' : 'flex-start' }}
      flexDirection={{ _: 'column', large: 'row' }}
    >
      {item.featured_tour_video.value[0] ? (
        <VideoPlayer
          maxWidth={{ large: '54rem' }}
          placeholderSrc={getKontentImage(item.featured_tour_placeholder.value[0]?.url, {
            width: 50,
          })}
          images={[
            getKontentImage(item.featured_tour_placeholder.value[0].url, { width: 700 }),
            getKontentImage(item.featured_tour_placeholder.value[0].url, { width: 1200 }),
            getKontentImage(item.featured_tour_placeholder.value[0].url, { width: 1000 }),
          ]}
          videoSrc={item.featured_tour_video.value[0]?.url}
          ratio={0.65}
          isFixedRatio
        />
      ) : (
        <Image
          className="aspect-[20/13] max-w-[54rem]"
          sources={[
            getKontentImage(item.featured_tour_placeholder.value[0].url, { width: 700 }),
            getKontentImage(item.featured_tour_placeholder.value[0].url, { width: 1200 }),
            getKontentImage(item.featured_tour_placeholder.value[0].url, { width: 1000 }),
          ]}
          placeholderSrc={getKontentImage(item.featured_tour_placeholder.value[0].url, {
            width: 50,
          })}
        />
      )}
      <Content
        position={{ _: 'relative', xlarge: 'absolute' }}
        height={{ _: 'auto' }}
        gridRow={{ large: 1 }}
        gridColumn={{ large: isTextVideo ? 1 : 2 }}
        bg="pampasLight"
        p={{
          _: '2rem 1rem 1rem',
          small: '3.5rem 12rem 3.5rem 3rem',
          large: '4rem 7.5rem 4rem 3rem',
        }}
        m={{ _: '0 auto' }}
        left={{ large: isTextVideo ? 0 : 'initial' }}
        right={{ large: isTextVideo ? 'initial' : 0 }}
        top={{ small: '-2rem', large: '50%' }}
        mb={{ _: 0, small: '-2rem', large: 0 }}
        width={{ _: '100%', small: 'calc(100% - 4rem)', large: '50vw', xlarge: '40rem' }}
        zIndex={2}
      >
        <OldHeading variant="h3" as="h3" mb={['1rem', '1.5rem']}>
          {item.title.value}
        </OldHeading>
        <OldText>{item.featured_tour_description.value}</OldText>
        <Checklist>
          {listItems.map((content, i) => (
            <li key={i}>{content}</li>
          ))}
        </Checklist>
        <Box mt="2rem">
          <LocalizedLink href={`/tours/${item.url_slug.value}`}>
            <Button as="link" variant="dark" className="w-full sm:!w-[auto]">
              {t('moreInfo')}
            </Button>
          </LocalizedLink>
        </Box>
      </Content>
    </Box>
  )
}

export default FeaturedTour
